import { debounce } from 'lodash'
import { useState, useRef, useEffect } from 'react'
import { formatDuration, intervalToDuration } from 'date-fns'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { GET_VIDEO_FRAMES } from '@/graphql/ads/video-thumbnail-queries'
import { GET_AD_ASSET_BY_ID, UPDATE_AD_ASSET_TRIM_DATA, GET_ASSET_STATUS } from '@/graphql/ads/ad-assets-queries'

export const useVideoTrim = (assetId) => {
    const videoRef = useRef(null)
    const timelineRef = useRef(null)

    // Video player state
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isBuffering, setIsBuffering] = useState(false)
    const [showThumbnail, setShowThumbnail] = useState(true)
    const [currentLength, setCurrentLength] = useState('')
    const [frames, setFrames] = useState([])
    const [adAsset, setAdAsset] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    // Trimming state
    const [trimStart, setTrimStart] = useState(0)
    const [trimEnd, setTrimEnd] = useState(100)

    // Dragging state
    const [isDragging, setIsDragging] = useState(false)
    const [dragType, setDragType] = useState(null) // 'start', 'end', or 'playhead'

    // GraphQL queries
    const [fetchFrames] = useLazyQuery(GET_VIDEO_FRAMES, {
        onCompleted: (data) => {
            if (data?.AdAsset?.videoFrames) {
                setFrames(data.AdAsset.videoFrames)
            }
        },
    })

    useQuery(GET_AD_ASSET_BY_ID, {
        fetchPolicy: 'network-only',
        variables: { id: assetId },
        skip: !assetId,

        onCompleted: (data) => {
            setAdAsset(data?.AdAsset?.adAsset)
            setIsLoading(false)
        },
    })

    useEffect(() => {
        if (adAsset) {
            const milliseconds = (adAsset.duration * (trimEnd - trimStart)) / 100
            const durationObj = intervalToDuration({
                start: 0,
                end: milliseconds,
            })
            setCurrentLength(formatDuration(durationObj))
        }
    }, [trimStart, trimEnd, adAsset])

    // Initialize trim values from database
    useEffect(() => {
        if (adAsset) {
            const adAssetData = adAsset

            if (adAssetData.trim_data?.start || adAssetData.trim_data?.end) {
                const { duration } = adAssetData
                const { start, end } = adAssetData.trim_data

                const startPercentage = (start / duration) * 100
                const endPercentage = (end / duration) * 100

                setTrimStart(startPercentage)
                setTrimEnd(endPercentage)
            } else {
                // If no trim data, show full video range
                setTrimStart(0)
                setTrimEnd(100)
            }
        }
    }, [adAsset])

    const {
        data: statusData,
        stopPolling,
        startPolling,
    } = useQuery(GET_ASSET_STATUS, {
        variables: { id: adAsset?.id },
        skip: !adAsset?.id || adAsset?.type !== 'video',
        pollInterval: adAsset?.status === 'generating_frames' ? 2000 : 0,
        onCompleted: (data) => {
            const status = data?.AdAsset?.adAsset?.status

            // If status changed from generating to done, fetch thumbnails
            if (status === 'done' || (status === null && adAsset?.status === 'generating_frames')) {
                fetchFrames({ variables: { assetId: adAsset.id } })
            }
        },
    })

    const currentStatus = statusData?.AdAsset?.adAsset?.status || adAsset?.status
    useEffect(() => {
        if (adAsset?.id && adAsset?.type === 'video') {
            if (currentStatus === 'generating_frames') {
                startPolling(2000)
            } else {
                stopPolling()
                fetchFrames({ variables: { assetId: adAsset.id } })
            }
        }

        return () => stopPolling()
    }, [adAsset?.id, currentStatus, fetchFrames, startPolling, stopPolling])

    // Mutation for saving trim data
    const [updateTrimData] = useMutation(UPDATE_AD_ASSET_TRIM_DATA)

    // Save trim data
    const saveTrimData = () => {
        const assetDuration = adAsset?.duration
        if (assetId && assetDuration) {
            // Convert percentages to actual time values in milliseconds
            // Since duration is already in milliseconds, we don't need to multiply by 1000
            const startTimeMs = Math.round((trimStart / 100) * assetDuration)
            const endTimeMs = Math.round((trimEnd / 100) * assetDuration)
            const trimData = {
                start: startTimeMs,
                end: endTimeMs,
            }

            updateTrimData({ variables: { assetId, trimData } })
        }
    }

    // Save trim data when values change
    useEffect(() => {
        saveTrimData()
    }, [trimStart, trimEnd])

    // Derived values
    const videoSrc = `${import.meta.env.VITE_S3_BUCKET_URL}${adAsset?.url}`
    const recommendedLength = 15 // seconds
    const isGeneratingFrames = currentStatus === 'generating_frames'

    // Limit frames for better timeline display (max 20 frames)
    const maxFramesToDisplay = 20
    const displayFrames =
        frames.length > maxFramesToDisplay
            ? frames
                  .filter((_, index) => index % Math.ceil(frames.length / maxFramesToDisplay) === 0)
                  .slice(0, maxFramesToDisplay)
            : frames

    const handleWaiting = useRef(debounce(() => setIsBuffering(true), 150)).current
    const handlePlaying = useRef(debounce(() => setIsBuffering(false), 150)).current

    useEffect(() => {
        return () => {
            handleWaiting.cancel()
            handlePlaying.cancel()
        }
    }, [])

    const calculateCurrentTime = () => {
        const video = videoRef.current
        const duration = adAsset?.duration
        const currentTimeMs = video.currentTime * 1000
        const percentage = (currentTimeMs / duration) * 100
        setCurrentTime(percentage)
    }

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const video = videoRef.current
            if (!video) return

            video.addEventListener('loadstart', handleWaiting)
            video.addEventListener('waiting', handleWaiting)
            video.addEventListener('playing', handlePlaying)
            video.addEventListener('timeupdate', calculateCurrentTime)
        })

        return () => cancelAnimationFrame(frame)
    }, [videoSrc])

    // Playback controls
    const togglePlay = () => {
        const video = videoRef.current
        if (!video) return

        if (isPlaying) {
            video.pause()
        } else {
            // If at or past trim end, seek back to trim start
            // TODO: fix
            const progress = (video.currentTime / video.duration) * 100
            if (progress >= trimEnd) {
                video.currentTime = (trimStart / 100) * video.duration
                setCurrentTime(trimStart)
            }
            video.play()
        }

        setShowThumbnail(false)
        setIsPlaying(!isPlaying)
    }

    const toggleMute = () => {
        const video = videoRef.current
        if (!video) return

        video.muted = !isMuted
        setIsMuted(!isMuted)
    }

    const seekToPosition = (percentage) => {
        const video = videoRef.current
        if (video && video.duration) {
            video.currentTime = (percentage / 100) * video.duration
            setCurrentTime(percentage)
        }
    }

    // Mouse drag handlers
    const handleMouseDown = (e, type) => {
        setIsDragging(true)
        setDragType(type)
        e.preventDefault()
    }

    const handleMouseMove = (e) => {
        if (!isDragging || !timelineRef.current) return

        const rect = timelineRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100))

        if (dragType === 'start') {
            setTrimStart(Math.min(percentage, trimEnd - 1))
        } else if (dragType === 'end') {
            setTrimEnd(Math.max(percentage, trimStart + 1))
        } else if (dragType === 'playhead') {
            seekToPosition(percentage)
        }
    }

    const handleMouseUp = () => {
        setIsDragging(false)
        setDragType(null)
    }

    // Mouse event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)

            return () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
            }
        }
    }, [isDragging, dragType, trimStart, trimEnd])

    const handleTimelineClick = (e) => {
        const rect = timelineRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const percentage = (x / rect.width) * 100
        seekToPosition(percentage)
    }

    return {
        // Refs
        videoRef,
        timelineRef,

        // State
        isPlaying,
        isMuted,
        currentTime,
        duration,
        trimStart,
        trimEnd,
        isDragging,
        showThumbnail,

        // Derived values
        videoSrc,
        frames,
        displayFrames,
        currentLength,
        recommendedLength,
        adAsset,

        // Loading states
        isLoading,
        isBuffering,
        isGeneratingFrames,

        // Actions
        togglePlay,
        toggleMute,
        seekToPosition,
        handleMouseDown,
        handleTimelineClick,

        // Setters (if you need direct control)
        setTrimStart,
        setTrimEnd,
    }
}
